import React from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  Text,
  Link,
  VStack,
  Divider,
  Image,
} from '@chakra-ui/react';
import {
  FiHome,
  FiUsers,
  FiCheckSquare,
  FiBarChart2,
  FiLogOut,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

// Navigation items for committee sidebar
const COMMITTEE_NAV_ITEMS = [
  { name: 'لوحة المعلومات', icon: FiHome, path: '/committee/dashboard' },
  { name: 'المرشحون', icon: FiUsers, path: '/committee/candidates' },
  { name: 'التقييمات', icon: FiCheckSquare, path: '/committee/evaluations' },
  { name: 'التقارير', icon: FiBarChart2, path: '/committee/reports' },
];

/**
 * Sidebar navigation component for committee users
 */
const CommitteeSidebar = ({ onClose, ...rest }) => {
  const { logout } = useAuth();
  const location = useLocation();

  return (
    <Box
      bg="white"
      borderRight="1px"
      borderColor="gray.200"
      w={{ base: 'full', md: 60 }}
      h="full"
      display="flex"
      flexDirection="column"
      {...rest}
    >
      <Flex direction="column" flex="1">
        <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
          <Text fontSize="2xl" fontWeight="bold" color="primary.500">
            اللجنة
          </Text>
          <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
        </Flex>

        <VStack spacing={1} align="stretch" px={3} flex="1" overflowY="auto" pb={20}>
          {COMMITTEE_NAV_ITEMS.map((navItem) => (
            <NavItem
              key={navItem.name}
              icon={navItem.icon}
              path={navItem.path}
              isActive={location.pathname === navItem.path}
            >
              {navItem.name}
            </NavItem>
          ))}

          <Divider my={4} />

          <NavItem
            icon={FiLogOut}
            path="/"
            onClick={(e) => {
              e.preventDefault();
              logout();
            }}
          >
            تسجيل الخروج
          </NavItem>
        </VStack>

        {/* Footer: Powered by */}
        <Box mt="auto" px={4} py={3} borderTop="1px" borderColor="gray.200" position="sticky" bottom={0} bg="white">
          <Flex align="center" justify="center">
            <Text fontSize="sm" ml={2}>
              تطوير بواسطة
            </Text>
            <Link href="https://zakhm.sa" isExternal>
              <Image
                src="/images/powerdByZakhm.svg"
                alt="Zakhm.sa"
                height="24px"
                fallbackSrc="https://majalisna-files-2025.s3.me-south-1.amazonaws.com/public-folder/zakhmlogo.svg"
              />
            </Link>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

// Individual navigation item component
const NavItem = ({ icon, children, path, isActive, onClick, ...rest }) => {
  return (
    <Link
      as={RouterLink}
      to={path}
      style={{ textDecoration: 'none' }}
      _focus={{ boxShadow: 'none' }}
      onClick={onClick}
    >
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        bg={isActive ? 'primary.50' : 'transparent'}
        color={isActive ? 'primary.500' : 'inherit'}
        fontWeight={isActive ? 'medium' : 'normal'}
        _hover={{
          bg: 'primary.50',
          color: 'primary.500',
        }}
        {...rest}
      >
        {icon && (
          <Icon
            ml="4" // Changed from mr to ml for RTL
            fontSize="16"
            as={icon}
          />
        )}
        {children}
      </Flex>
    </Link>
  );
};

export default CommitteeSidebar;
